/**
 * Gift card form validator
 * 
 * @copyright     (c) 2025 Gist Applications Inc.
 * @author        Greg Olive greg@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/_utils/FormValidator.js
 * Created:       Tue May 27 2025
 * Modified By:   Greg Olive
 * Last Modified: Fri Dec 19 2025
 */

import { Form } from "react-router-dom";

export const FormValidator = {
  /**
   * The errors object contains a set of error objects containing an error
   * message for a given field
   */
  errors: {},

  /**
   * The constraints object contains a set of checks that can be run on a given
   * form field to validate the given value.
   */
  constraints: {
    /**
     * @param {string} value
     * @returns {boolean}
     */
    notUndefined: (value) => {
      return typeof(value) != 'undefined';
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    notEmpty: (value) => {
      return value !== "";
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    notNull: (value) => {
      return typeof(value) != 'null';
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    notLengthZero: (value) => {
      return typeof(value) != 'undefined' && value !== null && value.length !== 0;
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    isEqualTo: (value, comparison) => {
      return value === comparison;
    },

    /**
     * 
     * @param {string} value
     * @param {string} comparison
     * @returns {boolean}
     */
    startsWith: (value, comparison) => {
      return (typeof value === "string" && value.startsWith(comparison));
    },

    /**
     * Checks if the value given is not undefined, empty, null or an empty string
     * @param {string} value
     * @returns {boolean}
     */
    hasValue: (value) => {
      return FormValidator.constraints.notUndefined(value) &&
             FormValidator.constraints.notEmpty(value) &&
             FormValidator.constraints.notNull(value) &&
             FormValidator.constraints.notLengthZero(value) &&
             value !== '---' && 
             value !== '';
    },

    /**
     * Checks if the country select value given is not undefined, empty, null or the blank option from country_option_tags '---'
     * @param {string} value
     * @returns {boolean}
     */
    hasCountryValue: (value) => {
      return FormValidator.constraints.notUndefined(value) &&
             FormValidator.constraints.notEmpty(value) &&
             FormValidator.constraints.notNull(value) &&
             value != '---';
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    isString: (value) => {
      return typeof(value) == 'string';
    },

    /**
     * Checks if the value is greater than or equal to the minimum value
     * @param {number} value
     * @param {number} min
     * @returns {boolean}
     */
    isGreaterThanOrEqualTo: (value, min) => {
      return value >= min;
    },

    /**
     * Checks if the value is less than or equal to the maximum value
     * @param {number} value
     * @param {number} max
     * @returns {boolean}
     */
    isLessThanOrEqualTo: (value, max) => {
      return value <= max;
    },

    /**
     * This function takes a given phone number and checks to see if
     * the phone number is valid based on the regex provided.  The
     * function will return true or false respectively.
     *
     * @param {string} phone -> The given phone number to be checked.
     * @returns {boolean}
     */
    isPhone: (phone) => {
      // Regular expression for phone input
      // ^\+[1-9]\d{1,14}$ comes from https://www.twilio.com/docs/glossary/what-e164
      // ^[\s()+-]*([0-9][\s()+-]*){6,20}$ comes from easy accounts
      var phoneRegex = /^[\s()+-]*([0-9][\s()+-]*){6,20}$/;

      return phoneRegex.test(phone);
    },

    /**
     * This function takes a given email and checks to see if the email
     * is a valid email based on the regex provided.  The function will
     * return true or false respectively.
     *
     * @param {string} email -> The given email to be checked.
     * @returns {boolean}
     */
    isEmail: (email) => {
      // Regular expression for email input
      // Don't use, as it does not work
      // var emailRegex = /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*(\.\w{2,})+$/;

      // Check if email contains @ and . characters and does not contain ,
      if (email?.indexOf('@') === -1 || email?.indexOf('.') === -1 || email?.indexOf(',') !== -1) {
        return false;
      }
      return true;
    },

    /**
     * Check if the value is a valid color string
     * @param {string} color
     * @returns {boolean}
     */
    isColorString: (color, allowAlpha = true) => {

      if (typeof color !== 'string') {
        return false;
      }

      const hexRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
      const rgbRegex = /^rgb\(\s*(?:\d{1,3}\s*,\s*){2}\d{1,3}\s*\)$/;
      const rgbaRegex = /^rgba\(\s*(?:\d{1,3}\s*,\s*){3}(?:0|1|0?\.\d+)\s*\)$/;

      // Check hex
      if (hexRegex.test(color)) {
        return true;
      }

      // Check rgb values range (0–255)
      if (rgbRegex.test(color)) {
        const nums = color.match(/\d+/g);
        if (!nums) return false;
        return nums.map(Number).every(n => n >= 0 && n <= 255);
      }

      // Check rgba values range (0–255 for RGB, 0–1 for alpha)
      if (rgbaRegex.test(color)) {
        const nums = color.match(/[\d.]+/g);
        if (!nums || nums.length !== 4) return false;

        const [r, g, b, a] = nums.map(Number);
        const valid = (
          r >= 0 && r <= 255 &&
          g >= 0 && g <= 255 &&
          b >= 0 && b <= 255 &&
          a >= 0 && a <= 1
        );
        return (valid && (allowAlpha || a === 1));
      }

      return false;

    },

    /**
     * Check if the value is a date in the future
     * @param {string} value
     * @param {number} leadTime
     * @returns {boolean}
     */
    isFutureDate: (value, leadTime) => {
      var requestedDate = new Date(value);
      var compareAtDate = new Date();

      if (leadTime) {
        compareAtDate.setDate(compareAtDate.getDate() + leadTime);
      }

      return requestedDate > compareAtDate;
    },

    /**
     * Check if the value is a date
     * @param {string} value
     * @returns {boolean}
     */
    isDate: (value) => {
      try {
        new Date(value);
        return true;
      } catch(e) {
        console.warn("Invalid date", value);
        return false;
      }
    },

    /**
     * @param {string} value
     * @returns {boolean}
     */
    isObject: (value) => {
      return typeof(value) == 'object';
    },

    /**
     * Checks if two values are equal strings
     * @param {*} value 
     * @param {*} comparison 
     */
    isEqualString: (value, comparison) => {
      return FormValidator.constraints.hasValue(value) &&
             FormValidator.constraints.hasValue(comparison) &&
             value.toString() === comparison.toString();
    },

    /**
     * Check if an array contains a value
     * @param {*} value 
     * @param {*} array 
     * @returns 
     */
    isMemberOf: (value, array) => {
      return array.some((el) => FormValidator.constraints.isEqualString(value, el));
    },

  },

  /**
   * Conver dot notation to bracket notation
   * @param {*} obj 
   * @param {*} path 
   * @returns 
   */
  convertDotToBracketNotation: (dotNotation) => {
    // Split the input string into parts based on the dot
    const parts = dotNotation.split('.');
    
    // Take the first part as the base, and prepare the rest of the parts
    // to be converted into bracket notation.
    if (parts.length === 1) {
      return dotNotation;  // Return as is if there's no dot in the string
    }
    
    // Start with the first part and then append each subsequent part in brackets
    let bracketNotation = parts[0];

    for (let i = 1; i < parts.length; i++) {
      bracketNotation += `[${parts[i]}]`;
    }
    
    return bracketNotation;
  },

  /**
   * Validation config is set up in dot notation to match the form html structure
   * @param {*} obj 
   * @param {*} path 
   * @returns 
   */
  getValueFromDotNotation: (obj, path) => {

    const index = path.indexOf('.');

    if (index === -1) {

      if (!obj) {
        console.warn("obj is null", obj, path);
        return "";
      }

      if (obj && typeof(obj[path]) === "undefined") {
        // console.warn("obj[path] is undefined", obj, path);
        return null;
      }

      // Handle symfony form data
      if (obj && obj[path] && typeof(obj[path].value) !== "undefined") {
        return obj[path].value;
      }

      // Handle regular form data
      return obj[path]
    }

    return FormValidator.getValueFromDotNotation(obj[path.slice(0, index)], path.slice(index + 1));

  },

  getValueFromInputName: (inputName) => {

    let input = document.querySelector(`input[type="text"][name="${inputName}"]`);

    if (input) {
      return input.value;
    }

    let textarea = document.querySelector(`textarea[name="${inputName}"]`);

    if (textarea) {
      return textarea.value;
    }

    let select = document.querySelector(`select[name="${inputName}"]`);

    if (select) {
      return select.value;
    }

    let radio = document.querySelector(`input[type="radio"][name="${inputName}"]:checked`);

    if (radio) {
      return radio.value;
    }

    let checkbox = document.querySelector(`input[type="checkbox"][name="${inputName}"]:checked`);

    if (checkbox) {
      return checkbox.value;
    }



    return null;
  },

  /**
   * The validate function takes in a set of rules and a set of data, then
   * performs a number of tests to validate the data.  If the data does not
   * pass the tests an error object is added to the errors object.  If the
   * data does pass the tests any previous errors for the data will be removed
   */
  validate: (inputRules, formData) => {
    
    let currentErrors = [];
    let currentErrorsObject = {};

    FormValidator.reset();

    if (!Array.isArray(inputRules)) {
      return false;
    }

    inputRules.forEach((inputRule, index) => {

      const inputName     = FormValidator.convertDotToBracketNotation(inputRule.name);
      const formDataValue = FormValidator.getValueFromDotNotation(formData, inputRule.name);
      const fieldName     = inputRule.name.split('.').pop();
      const errorKey      = inputRule.name.replace(".value", "").split('.').pop();
      let inputValue      = null;

      if (!formDataValue || formDataValue === "") {
        inputValue = FormValidator.getValueFromInputName(inputName);
      } else {
        inputValue = formDataValue;
      }

      const valid = inputRule.test(inputValue);

      // console.log(`Validating ${inputName}`, {
      //   value: inputValue,
      //   valid: valid
      // });


      if (!valid) {

        const currentError = {
          error: true,
          message: inputRule.error_message,
          value: inputValue,
        };

        FormValidator.errors[errorKey] = currentErrorsObject[errorKey] = currentError;
        currentErrors.push(currentError);

      }

    });

    
    if (currentErrors.length > 0) {
      console.warn("Fields incomplete", currentErrorsObject);
      localStorage.setItem('formErrors', JSON.stringify(currentErrorsObject));
      return false;
    }

    return true
  },

  getErrors: () => {

    const errors = localStorage.getItem('formErrors');

    if (errors) {
      return JSON.parse(errors);
    }

    return {};

  },

  /**
   * 
   * @param {*} field 
   */
  removeError: (field) => {

    const errors = FormValidator.getErrors();

    if (errors[field]) {
      delete errors[field];
    }

    localStorage.setItem('formErrors', JSON.stringify(errors));
  },

  /**
   * Reset the form validator to an empty state.
   */
  reset: () => {
    localStorage.removeItem('formErrors');
    FormValidator.errors = {};
  }
};
